import { useEffect, useRef, useState } from 'react';
import { consumeReplayMenuTutorialRequest } from './replayMenuTutorialStorage';

/**
 * When the player picks a tutorial in Lore → Tutorials, Lore writes the messageKey to
 * sessionStorage and navigates. This hook consumes it once and calls onReplay — the same
 * path as TutorialReplayButton (e.g. setShowTutorialStrategy(true)).
 *
 * @param {string} messageKey - e.g. 'strategy' (must match catalog + server keys)
 * @param {{ onReplay: () => void, enabled?: boolean, delayMs?: number }} options
 * @returns {{ isReplayRequest: boolean }}
 */
export function useMenuTutorialReplay(messageKey, { onReplay, enabled = true, delayMs = 600 }) {
  const [isReplayRequest, setIsReplayRequest] = useState(false);
  const onReplayRef = useRef(onReplay);
  onReplayRef.current = onReplay;
  const scheduledRef = useRef(false);

  useEffect(() => {
    if (!enabled || typeof onReplayRef.current !== 'function') {
      return undefined;
    }
    if (scheduledRef.current) {
      return undefined;
    }
    if (!consumeReplayMenuTutorialRequest(messageKey)) {
      return undefined;
    }

    scheduledRef.current = true;
    setIsReplayRequest(true);

    const timer = setTimeout(() => {
      onReplayRef.current();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [enabled, messageKey, delayMs]);

  return { isReplayRequest };
}

export default useMenuTutorialReplay;
